import { useEffect } from 'react'
import { OG_IMAGE_PATH, SITE_TITLE_SUFFIX, SITE_URL, type RouteMeta } from './site'

export function buildTitle(meta: Pick<RouteMeta, 'title' | 'key'>): string {
  // The home page already carries the product name in its title.
  return meta.key === 'home'
    ? `${meta.title} · ${'GodEngine'}`
    : `${meta.title} · ${SITE_TITLE_SUFFIX}`
}

export function canonicalFor(path: string): string {
  return `${SITE_URL}${path === '/' ? '/' : path.replace(/\/+$/, '')}`
}

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Keeps document metadata in sync on client-side navigation. The initial HTML
 * for every route is prerendered with the same values at build time, so
 * crawlers that do not execute JavaScript still get correct metadata.
 */
export function useDocumentMeta(meta: RouteMeta, options?: { noIndex?: boolean }) {
  const noIndex = options?.noIndex ?? false

  useEffect(() => {
    const title = buildTitle(meta)
    const canonical = canonicalFor(meta.path)

    document.title = title
    setMeta('meta[name="description"]', 'name', 'description', meta.description)
    setMeta('meta[name="robots"]', 'name', 'robots', noIndex ? 'noindex, follow' : 'index, follow')
    setLink('canonical', canonical)

    setMeta('meta[property="og:title"]', 'property', 'og:title', title)
    setMeta('meta[property="og:description"]', 'property', 'og:description', meta.description)
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonical)
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website')
    setMeta('meta[property="og:image"]', 'property', 'og:image', `${SITE_URL}${OG_IMAGE_PATH}`)
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title)
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description)
  }, [meta, noIndex])
}
